<template>
  <!-- Modern Glassmorphism Header -->
  <header
    class="modern-header fixed top-0 left-0 right-0 z-50 transition-all duration-500"
    :class="{ 'scrolled': isScrolled }"
  >
    <div class="container-custom mx-auto px-4">
      <!-- Main Header Content -->
      <div class="flex justify-between items-center transition-all duration-300 h-14 md:h-16" :class="{ 'lg:h-20': !isScrolled, 'lg:h-16': isScrolled }">
        <!-- Modern Logo with Company Name -->
        <NuxtLink to="/" class="logo-link group flex items-center gap-2 sm:gap-3">
          <img
            src="/Logo_Technomac-snc_PNG.png"
            alt="Technomac SNC"
            class="logo-image transition-all duration-300 h-8 md:h-9"
            :class="{ 'lg:h-12': !isScrolled, 'lg:h-10': isScrolled }"
          />
          <span class="hidden sm:flex items-baseline gap-1 whitespace-nowrap">
            <span class="font-oswald font-semibold text-base md:text-lg lg:text-xl tracking-wide" style="color: #2c3e50;">TECHNOMAC</span>
            <span class="font-oswald font-medium text-sm md:text-base lg:text-lg" style="color: #3498db;">SNC</span>
          </span>
        </NuxtLink>

        <!-- Desktop Navigation -->
        <nav class="hidden lg:flex items-center gap-1">
          <!-- Navigation Links -->
          <NuxtLink to="/" class="nav-link" :class="{ 'active': route.path === '/' }">
            <span class="nav-text">{{ $t('nav.home') }}</span>
          </NuxtLink>

          <!-- Machines: link + separate arrow to open categories dropdown -->
          <div class="relative inline-flex items-center">
            <NuxtLink to="/machines" class="nav-link" :class="{ 'active': route.path.startsWith('/machines') }">
              <span class="nav-text">{{ $t('nav.machines') }}</span>
            </NuxtLink>

            <button
              @click="showCategories = !showCategories"
              class="nav-dropdown-btn"
              :class="{ 'rotate': showCategories }"
              aria-expanded="showCategories"
              aria-haspopup="true"
              aria-label="Toggle machines categories"
            >
              <svg class="w-4 h-4 transition-transform duration-300" :class="{ 'rotate-180': showCategories }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 scale-95 -translate-y-2"
              enter-to-class="opacity-100 scale-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100 scale-100 translate-y-0"
              leave-to-class="opacity-0 scale-95 -translate-y-2"
            >
              <div v-if="showCategories" class="dropdown-menu">
                <div class="dropdown-header">
                  <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                  <span>Categories</span>
                </div>
                <div v-if="categoriesLoading" class="dropdown-loading">
                  <div class="loading-spinner"></div>
                  <span>Loading...</span>
                </div>
                <ul v-else class="dropdown-list">
                  <li v-for="cat in categories" :key="cat.id">
                    <button @click.prevent="goToCategory(cat)" class="dropdown-item">
                      <span class="dropdown-item-dot"></span>
                      {{ cat.name }}
                    </button>
                  </li>
                </ul>
              </div>
            </transition>
          </div>

          <NuxtLink to="/spare-parts" class="nav-link" :class="{ 'active': route.path.startsWith('/spare-parts') }">
            <span class="nav-text">{{ $t('nav.spareParts') }}</span>
          </NuxtLink>
          <NuxtLink to="/manutention" class="nav-link" :class="{ 'active': route.path === '/manutention' }">
            <span class="nav-text">{{ $t('nav.manutention') }}</span>
          </NuxtLink>
          <NuxtLink to="/about" class="nav-link" :class="{ 'active': route.path === '/about' }">
            <span class="nav-text">{{ $t('nav.about') }}</span>
          </NuxtLink>

          <!-- Contact CTA Button -->
          <NuxtLink to="/contact" class="nav-cta" :class="{ 'active': route.path === '/contact' }">
            <span>{{ $t('nav.contact') }}</span>
            <svg class="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </NuxtLink>

          <!-- Language Switcher -->
          <div class="ml-2 pl-4 border-l border-slate-200/50">
            <LanguageSwitcher />
          </div>
        </nav>

        <!-- Tablet Navigation (Medium screens) -->
        <nav class="hidden md:flex lg:hidden items-center gap-1">
          <NuxtLink to="/" class="nav-link" :class="{ 'active': route.path === '/' }">
            <span class="nav-text">{{ $t('nav.home') }}</span>
          </NuxtLink>

          <!-- Tablet: machines link + arrow dropdown -->
          <div class="relative inline-flex items-center">
            <NuxtLink to="/machines" class="nav-link" :class="{ 'active': route.path.startsWith('/machines') }">
              <span class="nav-text">{{ $t('nav.machines') }}</span>
            </NuxtLink>
            <button @click="showCategories = !showCategories" class="nav-dropdown-btn" :class="{ 'rotate': showCategories }">
              <svg class="w-4 h-4 transition-transform duration-300" :class="{ 'rotate-180': showCategories }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div v-if="showCategories" class="dropdown-menu">
                <div v-if="categoriesLoading" class="dropdown-loading">
                  <div class="loading-spinner"></div>
                  <span>Loading...</span>
                </div>
                <ul v-else class="dropdown-list">
                  <li v-for="cat in categories" :key="cat.id">
                    <button @click.prevent="goToCategory(cat)" class="dropdown-item">
                      <span class="dropdown-item-dot"></span>
                      {{ cat.name }}
                    </button>
                  </li>
                </ul>
              </div>
            </transition>
          </div>

          <NuxtLink to="/contact" class="nav-cta">
            <span>{{ $t('nav.contact') }}</span>
          </NuxtLink>

          <div class="ml-2 pl-3 border-l border-slate-200/50">
            <LanguageSwitcher />
          </div>
        </nav>

        <!-- Mobile Menu Button -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="mobile-menu-button md:hidden"
          aria-label="Toggle mobile menu"
        >
          <div class="hamburger" :class="{ 'open': mobileMenuOpen }">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 -translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-4"
    >
      <div v-if="mobileMenuOpen" class="mobile-menu">
        <div class="container-custom mx-auto px-4 py-4">
          <!-- Mobile Navigation Links -->
          <div class="mobile-nav-links">
            <NuxtLink to="/" @click="mobileMenuOpen = false" class="mobile-nav-link" :class="{ 'active': route.path === '/' }">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {{ $t('nav.home') }}
            </NuxtLink>

            <!-- Mobile: machines link plus expandable categories list -->
            <div class="mobile-nav-group">
              <div class="flex items-center">
                <NuxtLink to="/machines" @click="mobileMenuOpen = false" class="mobile-nav-link flex-1" :class="{ 'active': route.path.startsWith('/machines') }">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {{ $t('nav.machines') }}
                </NuxtLink>
                <button @click="showCategories = !showCategories" class="mobile-expand-btn" :class="{ 'expanded': showCategories }">
                  <svg class="w-5 h-5 transition-transform duration-300" :class="{ 'rotate-180': showCategories }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </div>
              <transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 max-h-0"
                enter-to-class="opacity-100 max-h-96"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 max-h-96"
                leave-to-class="opacity-0 max-h-0"
              >
                <div v-if="showCategories" class="mobile-submenu overflow-hidden">
                  <div v-if="categoriesLoading" class="text-sm text-slate-400 py-3 px-4">Loading...</div>
                  <NuxtLink
                    v-for="cat in categories"
                    :key="cat.id"
                    :to="`/machines?category=${cat.id}`"
                    @click="mobileMenuOpen = false"
                    class="mobile-submenu-link"
                  >
                    <span class="submenu-dot"></span>
                    {{ cat.name }}
                  </NuxtLink>
                </div>
              </transition>
            </div>

            <NuxtLink to="/spare-parts" @click="mobileMenuOpen = false" class="mobile-nav-link" :class="{ 'active': route.path.startsWith('/spare-parts') }">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ $t('nav.spareParts') }}
            </NuxtLink>

            <NuxtLink to="/manutention" @click="mobileMenuOpen = false" class="mobile-nav-link" :class="{ 'active': route.path === '/manutention' }">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
              </svg>
              {{ $t('nav.manutention') }}
            </NuxtLink>

            <NuxtLink to="/about" @click="mobileMenuOpen = false" class="mobile-nav-link" :class="{ 'active': route.path === '/about' }">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ $t('nav.about') }}
            </NuxtLink>
          </div>

          <!-- Mobile CTA -->
          <div class="mobile-cta-section">
            <NuxtLink to="/contact" @click="mobileMenuOpen = false" class="mobile-cta-button">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {{ $t('nav.contact') }}
            </NuxtLink>
            <div class="mobile-lang-switcher">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import LanguageSwitcher from '~/components/ui/LanguageSwitcher.vue'
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useSiteStore } from '~/stores/site'

const route = useRoute();
const siteStore = useSiteStore();
const mobileMenuOpen = ref(false);
const isScrolled = ref(false);

// Categories dropdown state
const categories = ref([])
const categoriesLoading = ref(true)
const showCategories = ref(false)

// Touch tracking for swipe gesture
let touchStartY = 0
let touchStartTime = 0

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

const fetchCategories = async () => {
  try {
    categoriesLoading.value = true
    const config = useRuntimeConfig()
    const apiBase = config.public?.apiBase || '/api/v1'
    const response = await $fetch(`${apiBase}/categories`)
    // Support APIs that return { data: [...] } or plain array
    categories.value = response.data || response || []
  } catch (error) {
    console.error('Header: failed to fetch categories', error)
    categories.value = []
  } finally {
    categoriesLoading.value = false
  }
}

const handleTouchStart = (e) => {
  touchStartY = e.touches[0].clientY
  touchStartTime = Date.now()
}

const handleTouchEnd = (e) => {
  if (!mobileMenuOpen.value) return

  const touchEndY = e.changedTouches[0].clientY
  const touchDuration = Date.now() - touchStartTime
  const touchDistance = touchStartY - touchEndY

  // Close menu if swiped up (> 30px upward) and fast enough (< 500ms)
  if (touchDistance > 30 && touchDuration < 500) {
    mobileMenuOpen.value = false
  }
}

const handleClickOutside = (e) => {
  // Get the header element
  const header = document.querySelector('.modern-header')
  // Close if click is outside the header
  if (header && !header.contains(e.target)) {
    mobileMenuOpen.value = false
  }
}

const goToCategory = (cat) => {
  // Navigate to machines page with category query
  const query = { category: cat.id }
  navigateTo({ path: '/machines', query })
  mobileMenuOpen.value = false
  showCategories.value = false
}


onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
  document.addEventListener("touchstart", handleTouchStart, { passive: true });
  document.addEventListener("touchend", handleTouchEnd, { passive: true });
  document.addEventListener("click", handleClickOutside, { passive: true });
  handleScroll(); // Initialize on mount
  fetchCategories()
  siteStore.fetchSettings()
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  document.removeEventListener("touchstart", handleTouchStart);
  document.removeEventListener("touchend", handleTouchEnd);
  document.removeEventListener("click", handleClickOutside);
});

// Close dropdown when route changes
watch(() => route.path, () => {
  showCategories.value = false
})
</script>

<style scoped>
/* Modern Glassmorphism Header */
.modern-header {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  font-family: 'Open Sans', system-ui, sans-serif;
}

.modern-header.scrolled {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
  border-bottom-color: rgba(226, 232, 240, 0.8);
}

/* Logo */
.logo-link {
  display: flex;
  align-items: center;
}

.logo-image {
  width: auto;
  transition: all 0.3s ease;
}

/* Desktop Navigation Links */
.nav-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.875rem;
  color: #475569;
  font-family: 'Open Sans', system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  overflow: hidden;
}

.nav-link .nav-text {
  position: relative;
  z-index: 1;
}

.nav-link::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(52, 152, 219, 0.1);
  border-radius: 8px;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.3s ease;
}

.nav-link:hover {
  color: #3498db;
}

.nav-link:hover::before {
  opacity: 1;
  transform: scale(1);
}

.nav-link.active {
  color: #2980b9;
  font-weight: 600;
}

.nav-link.active::before {
  opacity: 1;
  transform: scale(1);
  background: rgba(52, 152, 219, 0.15);
}

/* Dropdown Button */
.nav-dropdown-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #64748b;
  border-radius: 6px;
  transition: all 0.2s ease;
  margin-left: -4px;
}

.nav-dropdown-btn:hover {
  background: rgba(52, 152, 219, 0.1);
  color: #3498db;
}

/* Dropdown Menu */
.dropdown-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  min-width: 240px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  z-index: 100;
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.dropdown-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  color: #64748b;
  font-size: 0.875rem;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.dropdown-list {
  max-height: 280px;
  overflow-y: auto;
  padding: 8px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  font-size: 0.875rem;
  color: #374151;
  text-align: left;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(139, 92, 246, 0.08));
  color: #1d4ed8;
}

.dropdown-item-dot {
  width: 6px;
  height: 6px;
  background: #cbd5e1;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.dropdown-item:hover .dropdown-item-dot {
  background: #3498db;
  transform: scale(1.3);
}

/* Contact CTA Button */
.nav-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 1.25rem;
  background: #3498db;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 8px;
  transition: background 0.2s ease;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.25);
}

.nav-cta:hover {
  background: #2980b9;
  box-shadow: 0 2px 8px rgba(41, 128, 185, 0.35);
}

.nav-cta.active {
  background: #2980b9;
}

/* Mobile Menu Button */
.mobile-menu-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  background: rgba(241, 245, 249, 0.8);
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
}

@media (min-width: 768px) {
  .mobile-menu-button {
    display: none !important;
  }
}

.mobile-menu-button:hover {
  background: rgba(59, 130, 246, 0.1);
}

.hamburger {
  display: flex;
  flex-direction: column;
  width: 18px;
  height: 12px;
  position: relative;
}

.hamburger span {
  display: block;
  height: 2px;
  width: 100%;
  background: #3498db;
  border-radius: 2px;
  transition: all 0.3s ease;
  transform-origin: center;
}

.hamburger span:nth-child(1) { margin-bottom: 3px; }
.hamburger span:nth-child(2) { margin-bottom: 3px; width: 70%; }
.hamburger span:nth-child(3) { width: 85%; }

.hamburger.open span:nth-child(1) {
  transform: rotate(45deg) translate(3px, 3px);
}

.hamburger.open span:nth-child(2) {
  opacity: 0;
  transform: translateX(-10px);
}

.hamburger.open span:nth-child(3) {
  transform: rotate(-45deg) translate(4px, -4px);
  width: 100%;
}

/* Mobile Menu */
.mobile-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.mobile-nav-links {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  color: #374151;
  font-family: 'Open Sans', system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.mobile-nav-link svg {
  color: #64748b;
  transition: color 0.2s ease;
}

.mobile-nav-link:hover,
.mobile-nav-link:active {
  background: rgba(52, 152, 219, 0.1);
  color: #3498db;
}

.mobile-nav-link:hover svg,
.mobile-nav-link:active svg,
.mobile-nav-link.active svg {
  color: #3498db;
}

.mobile-nav-link.active {
  background: rgba(52, 152, 219, 0.15);
  color: #2980b9;
  font-weight: 600;
}

/* Mobile Nav Group (for expandable items) */
.mobile-nav-group {
  border-radius: 10px;
  overflow: hidden;
}

.mobile-expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  color: #64748b;
  border-radius: 6px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.mobile-expand-btn:hover {
  background: rgba(52, 152, 219, 0.1);
  color: #3498db;
}

.mobile-expand-btn.expanded {
  background: rgba(52, 152, 219, 0.1);
  color: #3498db;
}

/* Mobile Submenu */
.mobile-submenu {
  background: rgba(241, 245, 249, 0.5);
  border-radius: 6px;
  margin: 4px 0 4px 38px;
  padding: 4px;
}

.mobile-submenu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: #475569;
  font-size: 0.875rem;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.mobile-submenu-link:hover {
  background: white;
  color: #1d4ed8;
}

.submenu-dot {
  width: 5px;
  height: 5px;
  background: #94a3b8;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.mobile-submenu-link:hover .submenu-dot {
  background: #3498db;
}

/* Mobile CTA Section */
.mobile-cta-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  padding-bottom: 16px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
}

.mobile-cta-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  justify-content: center;
  padding: 10px 16px;
  background: #3498db;
  color: white;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: background 0.2s ease;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.25);
}

.mobile-cta-button:hover {
  background: #2980b9;
  box-shadow: 0 2px 8px rgba(41, 128, 185, 0.35);
}

.mobile-lang-switcher {
  flex-shrink: 0;
}

/* Scrollbar Styling */
.dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: transparent;
}

.dropdown-list {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
</style>
